import { useSyncExternalStore } from "react";
import { DEFAULT_TASK_PRIORITY } from "../components/Tasks/task-priority-config";

const initialState = {
    connection: "idle",
    connectionMessage: null,
    errorMessage: null,
    interimTranscript: "",
    messages: [],
    pendingMutation: false,
    saving: false,
    saved: false,
    knownUserCount: 0,
    activeUsers: [],
    taskDraft: {
        name: null,
        description: null,
        projectId: null,
        projectLabel: null,
        assigneeId: null,
        assigneeLabel: null,
        verifierId: null,
        verifierLabel: null,
        dueDate: null,
        dueDateLabel: null,
        priority: DEFAULT_TASK_PRIORITY,
        type: "standard"
    },
    taskPendingOptions: {},
    taskWorkspace: {
        visibleTasks: [],
        selectedTask: null,
        taskType: null,
        viewMode: null,
        currentUser: null
    },
    taskAction: {
        mode: "idle",
        singleDraft: null,
        multiDrafts: [],
        editProposal: null,
        lastResult: null,
        error: null
    },
    leavePending: [],
    leaveCursor: 0,
    leaveDecision: null,
    triage: {
        status: "idle",
        message: null,
        progress: [],
        result: null,
        jobId: null,
        error: null,
        selectedTask: null,
        planBlocks: []
    }
};

function createStore(initial) {
    let state = initial;
    const listeners = new Set();

    return {
        getState: () => state,
        setState: (patch) => {
            const next = typeof patch === "function" ? patch(state) : patch;
            state = { ...state, ...next };
            listeners.forEach((listener) => listener());
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        reset: () => {
            state = initial;
            listeners.forEach((listener) => listener());
        }
    };
}

function createActions(store) {
    return {
        setConnection(state, message) {
            store.setState({
                connection: state,
                connectionMessage: state === "error" ? null : message || null,
                errorMessage: state === "error" ? message || null : null
            });
        },
        setPendingMutation(value) {
            store.setState({ pendingMutation: value });
        },
        setInterimTranscript(text) {
            store.setState({ interimTranscript: text });
        },
        setMessages(messages) {
            store.setState({ messages: Array.isArray(messages) ? messages : [] });
        },
        startUserMessage(messageId) {
            if (!messageId) return;
            store.setState((state) => {
                if (state.messages.some((m) => m.id === messageId)) return state;
                return {
                    messages: [
                        ...state.messages,
                        {
                            id: messageId,
                            role: "user",
                            text: "",
                            partial: true,
                            pending: true,
                            ts: Date.now()
                        }
                    ],
                    interimTranscript: ""
                };
            });
        },
        updateUserMessage(messageId, text, { append = false, partial = false, itemId = null, removeIfEmpty = false } = {}) {
            if (!messageId) return;
            const value = text || "";
            store.setState((state) => {
                const index = state.messages.findIndex((m) => m.id === messageId || (itemId && m.itemId === itemId));
                const nextText = (current) => {
                    const combined = append ? `${current || ""}${value}` : value;
                    return partial ? combined.trimStart() : combined.trim();
                };

                if (index === -1) {
                    const trimmed = value.trim();
                    if (!trimmed && removeIfEmpty) return { interimTranscript: "" };
                    return {
                        messages: [
                            ...state.messages,
                            {
                                id: messageId,
                                role: "user",
                                text: trimmed,
                                itemId: itemId || null,
                                partial,
                                pending: !trimmed && partial,
                                ts: Date.now()
                            }
                        ],
                        interimTranscript: ""
                    };
                }

                const existing = state.messages[index];
                const updatedText = nextText(existing.text);
                if (!updatedText && removeIfEmpty) {
                    return {
                        messages: state.messages.filter((_, i) => i !== index),
                        interimTranscript: ""
                    };
                }

                return {
                    messages: state.messages.map((message, i) =>
                        i === index
                            ? {
                                  ...message,
                                  text: updatedText,
                                  itemId: itemId || message.itemId || null,
                                  partial,
                                  pending: !updatedText && partial
                              }
                            : message
                    ),
                    interimTranscript: ""
                };
            });
        },
        appendUserMessage(text, itemId) {
            const trimmed = (text || "").trim();
            if (!trimmed) return;
            store.setState((state) => {
                if (itemId && state.messages.some((m) => m.role === "user" && m.itemId === itemId)) {
                    return state;
                }
                const last = state.messages[state.messages.length - 1];
                if (last && last.role === "user" && last.text === trimmed && Date.now() - last.ts < 4000) {
                    return state;
                }
                return {
                    messages: [
                        ...state.messages,
                        {
                            id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                            role: "user",
                            text: trimmed,
                            itemId: itemId || null,
                            ts: Date.now()
                        }
                    ],
                    interimTranscript: ""
                };
            });
        },
        appendAssistantText(delta) {
            if (!delta) return;
            store.setState((state) => {
                const last = state.messages[state.messages.length - 1];
                // Only merge into the last assistant bubble if it's still
                // streaming (partial). If it's finalized (e.g., after a
                // barge-in or a completed prior response), start a NEW bubble
                // so consecutive responses don't get glued together with a
                // synthetic newline.
                if (last && last.role === "assistant" && last.partial) {
                    return {
                        messages: [
                            ...state.messages.slice(0, -1),
                            { ...last, text: last.text + delta }
                        ]
                    };
                }
                return {
                    messages: [
                        ...state.messages,
                        {
                            id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                            role: "assistant",
                            text: delta,
                            partial: true,
                            ts: Date.now()
                        }
                    ]
                };
            });
        },
        finalizeAssistantMessage() {
            store.setState((state) => {
                const last = state.messages[state.messages.length - 1];
                if (!last || last.role !== "assistant" || !last.partial) return state;
                return {
                    messages: [...state.messages.slice(0, -1), { ...last, partial: false }]
                };
            });
        },
        discardPartialAssistantMessage() {
            store.setState((state) => {
                const last = state.messages[state.messages.length - 1];
                if (!last || last.role !== "assistant" || !last.partial) return state;
                return { messages: state.messages.slice(0, -1) };
            });
        },
        patchTaskDraft(patch) {
            store.setState((state) => ({ taskDraft: { ...state.taskDraft, ...patch } }));
        },
        setTaskPendingOptions(field, options) {
            store.setState((state) => ({
                taskPendingOptions: { ...state.taskPendingOptions, [field]: options }
            }));
        },
        clearTaskPendingOptions(field) {
            store.setState((state) => {
                const next = { ...state.taskPendingOptions };
                delete next[field];
                return { taskPendingOptions: next };
            });
        },
        setTaskWorkspaceContext(context = {}) {
            store.setState({
                taskWorkspace: {
                    visibleTasks: Array.isArray(context.visibleTasks) ? context.visibleTasks : [],
                    selectedTask: context.selectedTask || null,
                    taskType: context.taskType || null,
                    viewMode: context.viewMode || null,
                    currentUser: context.currentUser || null
                }
            });
        },
        setTaskActionMode(mode) {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    mode: mode || "idle",
                    error: null
                }
            }));
        },
        setSingleTaskDraft(draft) {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    mode: "single-create",
                    singleDraft: draft || null,
                    error: null
                }
            }));
        },
        setMultiTaskDrafts(drafts) {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    mode: "multi-create",
                    multiDrafts: Array.isArray(drafts) ? drafts : [],
                    error: null
                }
            }));
        },
        setTaskEditProposal(proposal) {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    mode: proposal?.tasks?.length > 1 ? "bulk-edit" : "single-edit",
                    editProposal: proposal || null,
                    error: null
                }
            }));
        },
        setTaskActionResult(result) {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    lastResult: result || null,
                    error: null
                }
            }));
        },
        setTaskActionError(error) {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    error: error || null
                }
            }));
        },
        patchWorkspaceTasks(taskIds, patch) {
            const ids = new Set((Array.isArray(taskIds) ? taskIds : [taskIds]).map((id) => String(id)));
            store.setState((state) => ({
                taskWorkspace: {
                    ...state.taskWorkspace,
                    visibleTasks: state.taskWorkspace.visibleTasks.map((task) =>
                        ids.has(String(task.id)) ? { ...task, ...patch } : task
                    ),
                    selectedTask:
                        state.taskWorkspace.selectedTask && ids.has(String(state.taskWorkspace.selectedTask.id))
                            ? { ...state.taskWorkspace.selectedTask, ...patch }
                            : state.taskWorkspace.selectedTask
                }
            }));
        },
        resetTaskAction() {
            store.setState((state) => ({
                taskAction: {
                    ...state.taskAction,
                    mode: "idle",
                    singleDraft: null,
                    multiDrafts: [],
                    editProposal: null,
                    error: null
                }
            }));
        },
        setSaving(value) {
            store.setState({ saving: value });
        },
        setSaved(value) {
            store.setState({ saved: value });
        },
        setActiveUsers(users) {
            store.setState({
                activeUsers: Array.isArray(users) ? users : [],
                knownUserCount: Array.isArray(users) ? users.length : 0
            });
        },
        setLeavePending(requests) {
            store.setState({
                leavePending: Array.isArray(requests) ? requests : [],
                leaveCursor: 0
            });
        },
        setLeaveCursor(cursor) {
            store.setState({ leaveCursor: Math.max(0, Number(cursor) || 0) });
        },
        setLeaveDecision(decision) {
            store.setState({ leaveDecision: decision || null });
        },
        setTriageStatus(status, message = null) {
            store.setState((state) => ({
                triage: {
                    ...state.triage,
                    status,
                    message,
                    error: status === "error" ? message : null
                }
            }));
        },
        setTriageJob(jobId) {
            store.setState((state) => ({
                triage: {
                    ...state.triage,
                    jobId,
                    status: jobId ? "running" : state.triage.status
                }
            }));
        },
        appendTriageProgress(progress) {
            if (!progress) return;
            store.setState((state) => ({
                triage: {
                    ...state.triage,
                    progress: [...state.triage.progress, progress],
                    message: progress.message || state.triage.message,
                    status: "running"
                }
            }));
        },
        setTriageResult(result) {
            store.setState((state) => ({
                triage: {
                    ...state.triage,
                    status: "ready",
                    message: "Analysis ready.",
                    result,
                    planBlocks: Array.isArray(result?.plan_blocks) ? result.plan_blocks : state.triage.planBlocks,
                    error: null
                }
            }));
        },
        setTriagePlanBlocks(planBlocks) {
            store.setState((state) => ({
                triage: {
                    ...state.triage,
                    planBlocks: Array.isArray(planBlocks) ? planBlocks : []
                }
            }));
        },
        setTriageSelectedTask(task) {
            store.setState((state) => ({
                triage: {
                    ...state.triage,
                    selectedTask: task || null
                }
            }));
        },
        patchTriageTask(taskId, patch) {
            store.setState((state) => {
                const patchList = (items = []) =>
                    items.map((item) => (String(item.id) === String(taskId) ? { ...item, ...patch } : item));
                const result = state.triage.result
                    ? {
                          ...state.triage.result,
                          ranked_tasks: patchList(state.triage.result.ranked_tasks),
                          now: patchList(state.triage.result.now),
                          next: patchList(state.triage.result.next),
                          later: patchList(state.triage.result.later)
                      }
                    : null;
                return {
                    triage: {
                        ...state.triage,
                        result,
                        selectedTask:
                            state.triage.selectedTask && String(state.triage.selectedTask.id) === String(taskId)
                                ? { ...state.triage.selectedTask, ...patch }
                                : state.triage.selectedTask
                    }
                };
            });
        },
        resetAll() {
            store.reset();
        }
    };
}

const stores = new Map();

export function createVoiceStore(agentId = "default") {
    const store = createStore({ ...initialState });
    const actions = createActions(store);
    function useVoiceStore(selector) {
        return useSyncExternalStore(
            store.subscribe,
            () => selector(store.getState()),
            () => selector(initialState)
        );
    }
    return { agentId, store, actions, useVoiceStore };
}

export function getVoiceStore(agentId = "default") {
    if (!stores.has(agentId)) {
        stores.set(agentId, createVoiceStore(agentId));
    }
    return stores.get(agentId);
}
